import './SharedStyles.scss';
import './ModelConfig.scss';
import { ReactNode, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdAdd, MdDeleteOutline, MdEdit } from 'react-icons/md';
import { AlertDialog } from '../../Components/AlertDialog';
import { Button } from '../../Components/Button';
import { Appearance, Design, Intent, Size } from '../../Components/designConstants';
import { Dropdown } from '../../Components/Dropdown';
import { IconButton } from '../../Components/IconButton';
import { FullPageSpinner } from '../../Components/Spinner';
import { ToggleSwitch } from '../../Components/ToggleSwitch';
import { useSetError } from '../../redux/modals';
import {
  AuditConfig,
  AuditConfigDraft,
  MANDATORY_AD_STRENGTH_NOTE,
  MAX_CONFIG_NAME_LENGTH
} from './audit-config-constants';
import { AuditSchedulePicker } from './AuditSchedulePicker';
import {
  auditConfigToggleInput,
  draftFromAuditConfig,
  draftToAuditConfigInput,
  newAuditConfigDraft,
  summarizeAuditConfig,
  validateAuditConfigDraft
} from './audit-config-utils';
import {
  formatLastModified,
  getCopyTigerModelInstances,
  getCopyTigerStyleGuides
} from './copyTigerUtils';
import { CREATIVITY_OPTIONS } from './creativity-options';
import { useAuditConfigs, useDeleteAuditConfig, useSaveAuditConfig } from './useAuditConfigs';
import { ReviewerOption, useReviewerOptions } from './useReviewerOptions';

interface ModelConfigProps {
  company: string;
}

export const ModelConfig = ({ company }: ModelConfigProps) => {
  const { data: configs, isLoading, error } = useAuditConfigs(company);
  const saveConfig = useSaveAuditConfig(company);
  const deleteConfig = useDeleteAuditConfig(company);
  const setError = useSetError();
  const [draft, setDraft] = useState<AuditConfigDraft>();
  const [configToDelete, setConfigToDelete] = useState<AuditConfig>();

  const handleSave = async (nextDraft: AuditConfigDraft) => {
    try {
      await saveConfig.mutateAsync(draftToAuditConfigInput(nextDraft));
      setDraft(undefined);
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
  };

  const handleToggle = async (config: AuditConfig, enabled: boolean) => {
    try {
      await saveConfig.mutateAsync(auditConfigToggleInput(config, enabled));
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
  };

  const handleDelete = async () => {
    if (!configToDelete) return;
    try {
      await deleteConfig.mutateAsync(configToDelete.configID);
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
  };

  return (
    <>
      {draft ? (
        <AuditConfigForm
          company={company}
          existingNames={(configs ?? [])
            .filter(c => c.configID !== draft.configID)
            .map(c => c.name)}
          initialDraft={draft}
          isSaving={saveConfig.isPending}
          onCancel={() => setDraft(undefined)}
          onSave={handleSave}
        />
      ) : (
        <ConfigShell
          title='Model Config'
          subtitle='Which low-performing ads the scheduled audit flags, and how new copy is generated for them.'
          actions={
            <Button
              size={Size.SM}
              leadingElement={<MdAdd />}
              onClick={() => setDraft(newAuditConfigDraft())}
            >
              New config
            </Button>
          }
        >
          {isLoading && <FullPageSpinner transparent />}
          <AuditConfigList
            configs={configs}
            error={error}
            isLoading={isLoading}
            onDelete={setConfigToDelete}
            onEdit={config => setDraft(draftFromAuditConfig(config))}
            onToggle={handleToggle}
          />
        </ConfigShell>
      )}
      <AlertDialog
        intent={Intent.ERROR}
        show={Boolean(configToDelete)}
        onHide={() => setConfigToDelete(undefined)}
        icon={<MdDeleteOutline size={95} />}
        title='Delete audit config?'
        body={`Are you sure you want to delete "${configToDelete?.name}"? Its schedule will stop running. This action cannot be undone.`}
        primaryAction={{ onClick: handleDelete, label: 'Delete' }}
        secondaryAction={{ onClick: () => setConfigToDelete(undefined) }}
      />
    </>
  );
};

interface AuditConfigListProps {
  configs: AuditConfig[] | undefined;
  error: Error | null;
  isLoading: boolean;
  onDelete: (config: AuditConfig) => void;
  onEdit: (config: AuditConfig) => void;
  onToggle: (config: AuditConfig, enabled: boolean) => void;
}

const AuditConfigList = ({
  configs,
  error,
  isLoading,
  onDelete,
  onEdit,
  onToggle
}: AuditConfigListProps) => {
  if (isLoading) return null;
  if (error) return <div className='empty-state'>Unable to load audit configs.</div>;
  if (!configs || configs.length === 0) {
    return <div className='empty-state'>No audit configs yet. Create one to get started.</div>;
  }
  return (
    <div className='audit-config-list'>
      {configs.map(config => (
        <AuditConfigCard
          key={config.configID}
          config={config}
          onDelete={onDelete}
          onEdit={onEdit}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
};

interface AuditConfigCardProps {
  config: AuditConfig;
  onDelete: (config: AuditConfig) => void;
  onEdit: (config: AuditConfig) => void;
  onToggle: (config: AuditConfig, enabled: boolean) => void;
}

const AuditConfigCard = ({ config, onDelete, onEdit, onToggle }: AuditConfigCardProps) => (
  <div className='audit-config-card'>
    <div className='card-header'>
      <div className='card-title'>{config.name}</div>
      <div className='card-actions'>
        <ToggleSwitch
          label='Enabled'
          checked={config.enabled}
          onChange={() => onToggle(config, !config.enabled)}
        />
        <IconButton
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          size={Size.SM}
          aria-label={`Edit ${config.name}`}
          onClick={() => onEdit(config)}
        >
          <MdEdit />
        </IconButton>
        <IconButton
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          size={Size.SM}
          aria-label={`Delete ${config.name}`}
          onClick={() => onDelete(config)}
        >
          <MdDeleteOutline />
        </IconButton>
      </div>
    </div>
    <div className='card-description'>{summarizeAuditConfig(config)}</div>
    <div className='card-meta'>
      {config.lastRunAt && <span>Last run {new Date(config.lastRunAt).toLocaleString()}</span>}
      <span>Last updated on {formatLastModified(config.lastModified, config.lastUser)}</span>
    </div>
  </div>
);

interface AuditConfigFormProps {
  company: string;
  existingNames: string[];
  initialDraft: AuditConfigDraft;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (draft: AuditConfigDraft) => void;
}

const AuditConfigForm = ({
  company,
  existingNames,
  initialDraft,
  isSaving,
  onCancel,
  onSave
}: AuditConfigFormProps) => {
  const [draft, setDraft] = useState(initialDraft);
  const [showErrors, setShowErrors] = useState(false);
  const errors = validateAuditConfigDraft(draft, existingNames);
  const visibleErrors = showErrors ? errors : {};

  const update = <K extends keyof AuditConfigDraft>(key: K, value: AuditConfigDraft[K]) =>
    setDraft(prev => ({ ...prev, [key]: value }));

  const updateReviewers = (key: 'externalReviewers' | 'internalReviewers', emails: string[]) =>
    setDraft(prev => ({ ...prev, [key]: emails, reviewersEdited: true }));

  const updateModel = <K extends keyof AuditConfigDraft>(key: K, value: AuditConfigDraft[K]) =>
    setDraft(prev => ({ ...prev, [key]: value, modelEdited: true }));

  const { data: reviewerOptions } = useReviewerOptions(company);
  const [instances, setInstances] = useState<DropdownChoice[]>([]);
  const [styleGuides, setStyleGuides] = useState<DropdownChoice[]>([]);
  useEffect(() => {
    getCopyTigerModelInstances(company)
      .then(rows => setInstances(rows.map(row => ({ label: row.name, value: row.id }))))
      .catch(() => setInstances([]));
    getCopyTigerStyleGuides(company)
      .then(rows => setStyleGuides(rows.map(row => ({ label: row.name, value: row.id }))))
      .catch(() => setStyleGuides([]));
  }, [company]);

  const handleSubmit = () => {
    if (Object.keys(errors).length > 0) {
      setShowErrors(true);
      return;
    }
    onSave(draft);
  };

  return (
    <ConfigShell
      title={draft.configID ? 'Edit audit config' : 'New audit config'}
      subtitle='Generation alerts go to internal reviewers only.'
      footer={
        <>
          <Button
            size={Size.SM}
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button size={Size.SM} loading={isSaving} onClick={handleSubmit}>
            Save
          </Button>
        </>
      }
    >
      <div className='audit-config-form'>
        <ConfigField label='Name' htmlFor='audit-name' error={visibleErrors.name}>
          <Form.Control
            id='audit-name'
            className='config-input'
            maxLength={MAX_CONFIG_NAME_LENGTH}
            placeholder='e.g. High-spend underperformers'
            value={draft.name}
            onChange={e => update('name', e.target.value)}
          />
        </ConfigField>
        <ConfigSection
          title='Flag rules'
          description={`An ad is flagged when it clears the spend and age checks and is at or below either conversion check. ${MANDATORY_AD_STRENGTH_NOTE}`}
        >
          <div className='field-row'>
            <ConfigField
              label='Minimum spend ($)'
              hint='Ad must have spent at least this much in the lookback window.'
              error={visibleErrors.minSpend}
              htmlFor='audit-min-spend'
            >
              <Form.Control
                id='audit-min-spend'
                className='config-input'
                inputMode='decimal'
                value={draft.minSpend}
                onChange={e => update('minSpend', e.target.value)}
              />
            </ConfigField>
            <ConfigField
              label='Lookback window (days)'
              hint='Spend and conversion metrics are measured over this many days.'
              error={visibleErrors.lookbackDays}
              htmlFor='audit-lookback'
            >
              <Form.Control
                id='audit-lookback'
                className='config-input'
                inputMode='numeric'
                value={draft.lookbackDays}
                onChange={e => update('lookbackDays', e.target.value)}
              />
            </ConfigField>
            <ConfigField
              label='Max ads per run'
              hint='Only the highest-spend flagged ads, up to this many, are generated each run.'
              error={visibleErrors.maxAds}
              htmlFor='audit-max-ads'
            >
              <Form.Control
                id='audit-max-ads'
                className='config-input'
                inputMode='numeric'
                value={draft.maxAds}
                onChange={e => update('maxAds', e.target.value)}
              />
            </ConfigField>
            <ConfigField
              label='Active for at least (days)'
              hint='Skip ads newer than this.'
              error={visibleErrors.minActiveDays}
              htmlFor='audit-active-days'
            >
              <Form.Control
                id='audit-active-days'
                className='config-input'
                inputMode='numeric'
                value={draft.minActiveDays}
                onChange={e => update('minActiveDays', e.target.value)}
              />
            </ConfigField>
          </div>
          <div className='field-row'>
            <ConfigField
              label='Conversions at or below'
              hint='Set this, the conversion value, or both. At least one is required.'
              error={visibleErrors.minConversions}
              htmlFor='audit-conversions'
            >
              <Form.Control
                id='audit-conversions'
                className='config-input'
                inputMode='decimal'
                placeholder='Not used'
                value={draft.minConversions}
                onChange={e => update('minConversions', e.target.value)}
              />
            </ConfigField>
            <ConfigField
              label='Conversion value at or below ($)'
              hint='Blank = ignore conversion value.'
              error={visibleErrors.minConversionValue}
              htmlFor='audit-conversion-value'
            >
              <Form.Control
                id='audit-conversion-value'
                className='config-input'
                inputMode='decimal'
                placeholder='Not used'
                value={draft.minConversionValue}
                onChange={e => update('minConversionValue', e.target.value)}
              />
            </ConfigField>
          </div>
        </ConfigSection>
        <ConfigSection
          title='Model'
          description='How new copy is generated for flagged ads. Admin only.'
        >
          <div className='field-row'>
            <ConfigField label='Model' error={visibleErrors.instanceUuid}>
              <Dropdown
                size={Size.SM}
                ariaLabel='Model'
                options={instances}
                placeholder='Select a model...'
                value={draft.instanceUuid}
                onChange={value => updateModel('instanceUuid', value)}
              />
            </ConfigField>
            <ConfigField label='Brand guidelines'>
              <Dropdown
                size={Size.SM}
                ariaLabel='Brand guidelines'
                clearable
                options={styleGuides}
                placeholder='None'
                value={draft.styleGuideUuid}
                onChange={value => updateModel('styleGuideUuid', value ?? '')}
                onClear={() => updateModel('styleGuideUuid', '')}
              />
            </ConfigField>
          </div>
          <div className='field-row'>
            <ConfigField
              label='Creativity (temperature, top K, top P)'
              hint={CREATIVITY_OPTIONS[draft.creativity]?.description}
            >
              <Dropdown
                size={Size.SM}
                ariaLabel='Creativity'
                options={Object.keys(CREATIVITY_OPTIONS)}
                value={draft.creativity}
                onChange={value => updateModel('creativity', value)}
              />
            </ConfigField>
          </div>
          <ConfigField label='Additional instructions (optional)' htmlFor='audit-instructions'>
            <Form.Control
              as='textarea'
              id='audit-instructions'
              className='config-input config-textarea'
              maxLength={MAX_INSTRUCTIONS_LENGTH}
              rows={3}
              value={draft.additionalInstructions}
              onChange={e => updateModel('additionalInstructions', e.target.value)}
            />
            <div className='char-count'>
              {draft.additionalInstructions.length}/{MAX_INSTRUCTIONS_LENGTH}
            </div>
          </ConfigField>
        </ConfigSection>
        <ConfigSection title='Schedule' description='When this audit runs.'>
          <AuditSchedulePicker
            error={visibleErrors.schedule}
            value={draft.schedule}
            onChange={schedule => setDraft(prev => ({ ...prev, schedule, scheduleEdited: true }))}
          />
        </ConfigSection>
        <ConfigSection
          title='Reviewers'
          description='Generation alerts are Slack DMs to internal reviewers only. Sending a run to the client is a separate action.'
        >
          <ConfigField
            label='Internal reviewers'
            hint='Leave empty to let any internal user edit flagged ads. The creator is added automatically.'
            error={visibleErrors.internalReviewers}
          >
            <Dropdown
              size={Size.SM}
              ariaLabel='Internal reviewers'
              multiSelect
              searchable
              clearable
              options={withSelected(draft.internalReviewers, reviewerOptions?.internal)}
              placeholder='Select internal reviewers...'
              value={draft.internalReviewers}
              onChange={emails => updateReviewers('internalReviewers', emails)}
            />
          </ConfigField>
          <ConfigField
            label='Client reviewers'
            hint='Client contacts for this company. Stored as a comma-separated list.'
            error={visibleErrors.externalReviewers}
          >
            <Dropdown
              size={Size.SM}
              ariaLabel='Client reviewers'
              multiSelect
              searchable
              clearable
              options={withSelected(draft.externalReviewers, reviewerOptions?.external)}
              placeholder='Select client reviewers...'
              value={draft.externalReviewers}
              onChange={emails => updateReviewers('externalReviewers', emails)}
            />
          </ConfigField>
        </ConfigSection>
        <ConfigSection
          title='Status'
          description='Disabled configs keep their settings but do not run.'
        >
          <div className='enabled-toggle'>
            <ToggleSwitch
              label='Enabled'
              checked={draft.enabled}
              onChange={() => update('enabled', !draft.enabled)}
            />
          </div>
        </ConfigSection>
      </div>
    </ConfigShell>
  );
};

type DropdownChoice = ReviewerOption;

// Keeps already-saved emails selectable even when the user list no longer returns them.
const withSelected = (selected: string[], options: ReviewerOption[] = []): ReviewerOption[] => [
  ...options,
  ...selected
    .filter(email => !options.some(option => option.value === email))
    .map(email => ({ label: email, value: email }))
];

const MAX_INSTRUCTIONS_LENGTH = 500;

interface ConfigShellProps {
  actions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  subtitle: string;
  title: string;
}

// The same card shell the Style Guide and Train Models tabs use (SharedStyles.scss).
const ConfigShell = ({ actions, children, footer, subtitle, title }: ConfigShellProps) => (
  <div className='styleguide-container model-config'>
    <div className='bpm-styleguide'>
      <div className='styleguide-header'>
        <div className='header-text'>
          <div className='styleguide-title'>{title}</div>
          <div className='styleguide-subtitle'>{subtitle}</div>
        </div>
        {actions && <div className='styleguide-header-actions'>{actions}</div>}
      </div>
      <div className='styleguide-content'>
        <div className='styleguide-main'>
          <div className='main-content'>{children}</div>
          {footer && <div className='main-footer'>{footer}</div>}
        </div>
      </div>
    </div>
  </div>
);

interface ConfigFieldProps {
  children: ReactNode;
  error?: string;
  hint?: string;
  htmlFor?: string;
  label: string;
}

const ConfigField = ({ children, error, hint, htmlFor, label }: ConfigFieldProps) => (
  <div className='config-field'>
    <label className='field-label' htmlFor={htmlFor}>
      {label}
    </label>
    {hint && <div className='field-hint'>{hint}</div>}
    {children}
    {error && <div className='field-error'>{error}</div>}
  </div>
);

interface ConfigSectionProps {
  children: ReactNode;
  description?: string;
  title: string;
}

const ConfigSection = ({ children, description, title }: ConfigSectionProps) => (
  <section className='config-section'>
    <div className='section-header'>
      <div className='section-title'>{title}</div>
      {description && <div className='section-description'>{description}</div>}
    </div>
    <div className='section-body'>{children}</div>
  </section>
);
